// src/pages/BlueHandsOpportunityIndians.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faBuilding,
  faHammer,
  faUsers,
  faArrowUpRightDots,
  faLightbulb,
  faFlag,
} from "@fortawesome/free-solid-svg-icons";

function BlueHandsOpportunityIndians() {
  const top10 = [
    { country: "Saudi Arabia", note: "695,000 Indian ECR workers (Jan 2020–Jun 2025).", source: "Coastal Digest" },
    { country: "UAE", note: "25% rise in demand for skilled blue-collar workers (2023–24).", source: "Economic Times" },
    { country: "Kuwait", note: "201,000 Indian workers (2020–25).", source: "Coastal Digest" },
    { country: "Qatar", note: "153,000 Indian blue-collar workers recorded (2020–25).", source: "Coastal Digest" },
    { country: "Oman", note: "116,000 Indian workers (2020–25).", source: "Coastal Digest" },
    { country: "Bahrain", note: "Longstanding MoU on labour development with India.", source: "MEA" },
    { country: "Jordan", note: "Part of India’s labour mobility MoU network.", source: "MEA" },
    { country: "Israel", note: "Active hiring under India–Israel employment MoU (construction).", source: "MEA" },
    { country: "Germany", note: "Recruiting Indian workers to address construction labour shortage.", source: "Business Standard" },
    { country: "Japan", note: "Identified as key partner for skilled/semi-skilled Indian workers.", source: "MEA" },
  ];

  const next10 = [
    { country: "Taiwan", note: "MoU signed for Indian worker mobility; construction, manufacturing.", source: "MEA" },
    { country: "Italy", note: "Migration & Mobility Agreement signed; high European demand.", source: "Sansad" },
    { country: "Austria", note: "Labour mobility agreement signed with India.", source: "MEA" },
    { country: "Denmark", note: "Mobility MoU signed, openings expected.", source: "MEA" },
    { country: "Portugal", note: "2021 MoU for Indian worker recruitment.", source: "MEA" },
    { country: "Malaysia", note: "Recruitment & repatriation MoU for Indian workers.", source: "MEA" },
    { country: "Russia", note: "Industrial workforce shortage, plans to hire Indian workers.", source: "Economic Times" },
    { country: "Greece", note: "Emerging demand for construction, agriculture & hospitality sectors.", source: "Business Standard" },
    { country: "Finland", note: "Listed among seven key countries for rising Indian worker demand.", source: "Financial Express" },
    { country: "Spain", note: "European market shortage; active interest in Indian labour.", source: "Business Standard" },
  ];

  const takeaways = [
    "Gulf/Middle East remains strongest region (Saudi Arabia, UAE, Kuwait, Qatar, Oman).",
    "Europe & East Asia are emerging second-wave regions (Germany, Japan, Taiwan, Russia, etc).",
    "Highlight skills certification, trade readiness & regional specialization (GCC vs Europe vs Asia).",
    "Build country-specific job streams (e.g., GCC Construction, Europe Skilled Labour, East Asia Manufacturing).",
    "Focus on language, visa readiness & employer compliance for new regions.",
  ];

  return (
    <div className="min-h-screen bg-black text-gray-100 py-6 px-3 sm:px-6">
      <div className="max-w-5xl mx-auto bg-gray-900 rounded-2xl shadow-2xl border border-blue-800 p-6 sm:p-10">
        <div className="text-center mb-10">
          <FontAwesomeIcon icon={faGlobe} className="text-emerald-400 text-4xl mb-3" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">
            Global Job Opportunities for Indian Blue-Collar Workers
          </h1>
          <p className="text-gray-400 mt-3 text-base sm:text-lg">
            Discover where Indian workforce demand is the highest — today and tomorrow.
          </p>
          <p className="text-yellow-200 mt-3 text-lg">
            BlueHands by Think5Whys is a complete ecosystem for Blue Collar Workers and other Stakeholders
          </p>          
        </div>

        {/* Top 10 Section */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon icon={faHammer} className="text-yellow-400 text-xl" />
            <h2 className="text-lg lg:text-xl font-semibold text-emerald-400">Top 10 Strong Demand Countries</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {top10.map((item, i) => (
              <div
                key={i}
                className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-emerald-500 transition"
              >
                <h3 className="text-m lg:text-lg font-semibold text-yellow-300">{i + 1}. {item.country}</h3>
                <p className="text-gray-300 text-sm mt-1">{item.note}</p>
                <p className="text-xs text-gray-500 mt-1 italic">Source: {item.source}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Next 10 Section */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon icon={faArrowUpRightDots} className="text-blue-400 text-xl" />
            <h2 className="text-lg lg:text-xl font-semibold text-blue-400">More Emerging / Opportunity Countries</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {next10.map((item, i) => (
              <div
                key={i}
                className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-blue-500 transition"
              >
                <h3 className="text-m lg:text-lg font-semibold text-emerald-300">{i + 11}. {item.country}</h3>
                <p className="text-gray-300 text-sm mt-1">{item.note}</p>
                <p className="text-xs text-gray-500 mt-1 italic">Source: {item.source}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Takeaways */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon icon={faLightbulb} className="text-yellow-400 text-xl" />
            <h2 className="text-lg lg:text-xl font-semibold text-yellow-400">Key Takeaways</h2>
          </div>
          <ul className="list-disc pl-6 space-y-2 text-gray-300">
            {takeaways.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>

        {/* Footer */}
        <div className="text-center mt-10 text-gray-500 text-sm border-t border-gray-700 pt-4">
          <p>
            Data Source: MEA, The Economic Times, Business Standard, Coastal Digest, Financial Express, Sansad Reports.
          </p>
          <p className="mt-2 text-gray-600">
            © 2025 Think5Whys • BlueHands Global Opportunities Board 🌐
          </p>
        </div>
      </div>
    </div>
  );
}

export default BlueHandsOpportunityIndians;
